import { useState, useRef, useEffect } from 'react';
import { scanUrl, getScanProgress, probeUrl, footprintUrl, startProbeJob, waitForProbeJob, getHistory, getProbeEngines, engineName, type ProbeEngine, type ProbeJob, type ScanStep } from './api';
import { useAccount } from './account/context';
import { saveScanRun, getAccountScanRuns, type QuerySet } from './account';
import type { AnalyzedPage, FaqItem, FootprintResult, HistoryResult, ProbeEntry, ScanResult } from './types';
import Dashboard from './components/Dashboard';
import VisibilityPanel from './components/VisibilityPanel';
import ClaimYourPages from './components/ClaimYourPages';
import PanelBoundary from './components/PanelBoundary';
import RunRivals from './components/RunRivals';
import FootprintPanel from './components/FootprintPanel';
import ScanWizard, { type WizardChoice } from './components/ScanWizard';
import { Stat, StatRow, SectionLabel } from './components/ui';
import ScheduleToggle from './components/ScheduleToggle';
import PagesPanel from './components/PagesPanel';
import Timeline from './components/Timeline';
import Tabs, { type TabDef } from './components/Tabs';
import ScanProgress from './components/ScanProgress';
import LiveTestOverlay from './components/LiveTestOverlay';
import About from './components/About';
import FixIt from './components/FixIt';
import FontSize from './components/FontSize';
import Scoreboard from './components/Scoreboard';
import Pricing from './components/Pricing';
import HeroLoop from './components/HeroLoop';
import RecentScans from './components/RecentScans';
import Faq from './components/Faq';
import { usePricing } from './pricing';

type TabId = 'readiness' | 'visibility' | 'footprint' | 'fixit';

function isLimitError(msg: string): boolean {
  return /limit reached|cost cap/i.test(msg);
}

/**
 * A dropped connection or an unreachable engine. The browser reports these as
 * "Failed to fetch" / ERR_CONNECTION_CLOSED, which shows up as nothing happening
 * at all — so it gets its own, plainly-worded message.
 */
function isConnectionError(msg: string): boolean {
  return /failed to fetch|networkerror|connection|econnreset|timed out|timeout|load failed|502|503|504/i.test(msg);
}

function hostOfUrl(u: string): string {
  try { return new URL(/^https?:\/\//i.test(u) ? u : `https://${u}`).hostname.replace(/^www\./, ''); } catch { return u; }
}

function friendlyError(msg: string): string {
  return isConnectionError(msg)
    ? 'The AI engine didn’t respond in time and the connection dropped. Nothing was lost and your questions are saved. This is usually the engine being busy, so please try again in a few minutes.'
    : msg;
}

/**
 * The scan page.
 *
 * `embedded` is set when this renders as a page inside the signed-in dashboard
 * rather than as the standalone marketing surface. It suppresses the right-hand
 * rail, because the dashboard already has one on the left and two rails is one
 * too many, and drops the outer padding the shell is already providing.
 */
export default function App({ embedded = false }: { embedded?: boolean } = {}): JSX.Element {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [history, setHistory] = useState<HistoryResult | null>(null);
  const [rescanning, setRescanning] = useState(false);

  const [tab, setTab] = useState<TabId>('readiness');
  const [showAbout, setShowAbout] = useState(false);
  const [showPricing, setShowPricing] = useState(false);

  // Account/plan come from shared context (header widget drives sign-in/out).
  const { account, setModal } = useAccount();
  const pricing = usePricing();
  const plan: 'free' | 'pro' = account?.subscription.tier === 'pro' ? 'pro' : 'free';
  // How many questions to ask is the SERVER's decision — it is admin-tunable and
  // the plan cap is enforced there anyway. Hardcoding it here silently overrode a
  // correctly-configured server (a raised FOOTPRINT_QUESTIONS kept producing the
  // old count because this constant was sent as `n`). Only the free ceiling stays
  // client-side, to keep the review UI honest before the response lands.
  const questionCount = plan === 'pro' ? undefined : 5;
  const freeCap = 5;

  // Which AI engine the visitor probes with (option C). Populated from the admin registry.
  // Domains already scanned, offered next to the input so the client picks
  // rather than retypes — and so a domain's saved question set is findable.
  const [scannedDomains, setScannedDomains] = useState<string[]>([]);
  // The set the current result asked — named on the run and offered for re-checks.
  const [runSet, setRunSet] = useState<Pick<QuerySet, 'id' | 'name' | 'domain'> | null>(null);
  // The wizard is the way in; with a result on screen it folds to a summary line.
  const [wizardOpen, setWizardOpen] = useState(false);
  // What the readiness scan is doing right now, as reported by the server.
  const [scanSteps, setScanSteps] = useState<ScanStep[]>([]);
  const [engines, setEngines] = useState<ProbeEngine[]>([]);
  const [engine, setEngine] = useState<'openai' | 'gemini'>('openai');
  useEffect(() => {
    void getProbeEngines().then((es) => {
      setEngines(es);
      /*
        Don't clobber a restored scan's engine (see the restore effect below).

        This guarded on `aivs_scan`, which nothing has written since the blob
        moved to `aivs_scan2`, so it always read null and the default engine
        overwrote the restored one every time. The guard has been off since the
        key changed.
      */
      // ChatGPT is the default engine; Gemini only when it is all there is.
      if (es[0] && !localStorage.getItem('aivs_scan2')) setEngine(es.some((e) => e.engine === 'openai') ? 'openai' : es[0].engine);
    }).catch(() => {});
  }, []);

  const [probes, setProbes] = useState<ProbeEntry[]>([]);
  const [probeBrand, setProbeBrand] = useState<{ name: string; domain: string; aliases?: string[] } | null>(null);
  const [probePages, setProbePages] = useState<AnalyzedPage[]>([]);
  const [probeLoading, setProbeLoading] = useState(false);
  const [probeError, setProbeError] = useState<string | null>(null);
  const [probeEngineLabel, setProbeEngineLabel] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const [footprint, setFootprint] = useState<FootprintResult | null>(null);
  const [footprintLoading, setFootprintLoading] = useState(false);
  const [footprintError, setFootprintError] = useState<string | null>(null);

  // Semi-automatic mode (D-28) now lives in the wizard: the questions are the
  // client's own saved set, chosen before anything is searched.
  // Live progress of the background run (D-35).
  const [jobProgress, setJobProgress] = useState<{ done: number; total: number } | null>(null);

  // Set when the live AI tests were skipped because AI can't read the site (#3):
  // robots blocks the citing bots, or a block/consent wall left no readable text.
  const [blockedNotice, setBlockedNotice] = useState<'ai-blocked' | 'thin-content' | 'http-error' | null>(null);

  // FAQ draft lives here (not in FixIt) so it survives tab switches (#5).
  const [faq, setFaq] = useState<FaqItem[]>([]);
  // Bumped after each saved run so the recent-scans table refetches.
  const [scanCount, setScanCount] = useState(0);

  useEffect(() => {
    if (account) void getAccountScanRuns({ page: 1 }).then((d) => setScannedDomains(d.domains)).catch(() => {});
  }, [account, scanCount]);

  /**
   * Signing out clears the screen back to the welcome state.
   *
   * The results are held in component state, so they survived the session that
   * produced them: after signing out the last scan was still sitting there, on
   * a machine that might not be the owner's. That is somebody's brand, their
   * competitors and the pages AI reads about them, left up for whoever sits down
   * next — and it also made "sign out" look like it had not worked.
   *
   * Guarded on the transition from signed-in to signed-out so it never wipes a
   * scan a signed-out visitor is legitimately looking at.
   */
  const wasSignedIn = useRef(false);
  useEffect(() => {
    if (account) { wasSignedIn.current = true; return; }
    if (!wasSignedIn.current) return;
    wasSignedIn.current = false;

    setResult(null);
    setHistory(null);
    setProbes([]);
    setProbeBrand(null);
    setProbePages([]);
    setProbeEngineLabel(null);
    setProbeError(null);
    setFootprint(null);
    setFootprintError(null);
    setFaq([]);
    setBlockedNotice(null);
    setJobProgress(null);
    setUrl('');
    setRunSet(null);
    setWizardOpen(false);
    setScannedDomains([]);
    setTab('readiness');
    window.scrollTo({ top: 0 });
  }, [account]);

  // Persist the current scan (readiness + probes + footprint + questions) for the
  // tab session, so leaving to "My scans & progress" and coming back keeps results.
  useEffect(() => {
    try {
      const raw = localStorage.getItem('aivs_scan2');
      if (!raw) return;
      const s = JSON.parse(raw);
      if (!s.result) return;
      // Deliberately NOT restoring the URL into the input. Refilling it made the
      // next visit look like it was about to re-scan the last site, when the
      // common case is scanning a different one. The result below is still
      // restored; the input starts clean.
      void s.url;
      setResult(s.result);
      setRunSet(s.runSet ?? null);
      setProbes(s.probes ?? []);
      setProbeBrand(s.probeBrand ?? null);
      setProbePages(s.probePages ?? []);
      setProbeEngineLabel(s.probeEngineLabel ?? null);
      setFootprint(s.footprint ?? null);
      setFaq(s.faq ?? []);
      if (s.engine) setEngine(s.engine);
      if (s.tab) setTab(s.tab);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    try {
      if (result) localStorage.setItem('aivs_scan2', JSON.stringify({ url, result, probes, probeBrand, probePages, probeEngineLabel, footprint, faq, engine, tab, runSet }));
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, probes, probeBrand, probePages, probeEngineLabel, footprint, faq, engine, tab, runSet]);

  // Read-first nudge (#2): has the visitor opened "How AI citation really works"?
  const [hasReadAbout, setHasReadAbout] = useState(() => {
    try { return localStorage.getItem('aivs_read_about') === '1'; } catch { return false; }
  });
  const [readModal, setReadModal] = useState(false);
  // The wizard's choice, held while the read-first modal is up.
  const [pendingRun, setPendingRun] = useState<WizardChoice | null>(null);
  function markRead(): void {
    setHasReadAbout(true);
    try { localStorage.setItem('aivs_read_about', '1'); } catch { /* ignore */ }
  }
  function openAbout(): void {
    markRead();
    setShowAbout(true);
    setShowPricing(false);
    setReadModal(false);
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
  }
  function goToScan(): void {
    setShowAbout(false);
    setShowPricing(false);
    setWizardOpen(true);
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
  }
  function goToFaq(): void {
    setShowAbout(false);
    setShowPricing(false);
    setTimeout(() => document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  }
  // Sidebar nav from the #account page hands off an action via sessionStorage.
  useEffect(() => {
    const pending = sessionStorage.getItem('aivs_pending');
    if (!pending) return;
    sessionStorage.removeItem('aivs_pending');
    if (pending === 'about') openAbout();
    else if (pending === 'faq') goToFaq();
    else if (pending === 'scan') goToScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const limitHit =
    (error && isLimitError(error)) ||
    (probeError && isLimitError(probeError)) ||
    (footprintError && isLimitError(footprintError));
  // Show the server's own wording — it names the plan, the number and when it resets.
  const limitMessage = [error, probeError, footprintError].find((m) => m && isLimitError(m)) ?? null;

  /** The wizard's last step. Everyone is signed in by now (step 1 asks). */
  function onWizardRun(choice: WizardChoice): void {
    if (!account) { setModal('signin'); return; }
    // Nudge first-timers to read how AI citation works before scanning (#2).
    if (!hasReadAbout) {
      setPendingRun(choice);
      setReadModal(true);
      return;
    }
    void doScan(choice);
  }

  async function doScan(choice: WizardChoice): Promise<void> {
    if (loading) return;
    setReadModal(false);
    setPendingRun(null);
    setWizardOpen(false);
    setUrl(choice.url);
    setEngine(choice.engine);
    setRunSet({ id: choice.set.id, name: choice.set.name, domain: choice.set.domain });
    setLoading(true);
    setError(null);
    setResult(null);
    setHistory(null);
    setProbes([]);
    setProbeBrand(null);
    setProbePages([]);
    setProbeError(null);
    setProbeEngineLabel(null);
    setFootprint(null);
    setFootprintError(null);
    setBlockedNotice(null);
    setFaq([]);
    setTab('readiness');
    setScanSteps([]);

    // 1) Readiness first — fast, shown immediately. Its steps are polled while
    //    it runs, so the slow AI read is narrated instead of looking frozen.
    const progressId = crypto.randomUUID();
    let polling = true;
    const poll = async (): Promise<void> => {
      while (polling) {
        try {
          const p = await getScanProgress(progressId);
          if (polling && p.steps.length) setScanSteps(p.steps);
        } catch { /* a missed poll only delays the next line */ }
        await new Promise((r) => setTimeout(r, 900));
      }
    };
    void poll();
    let scan;
    try {
      scan = await scanUrl(choice.url, false, progressId);
      setResult(scan);
      void refreshHistory(scan.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'scan failed');
      setLoading(false);
      return;
    } finally {
      polling = false;
    }
    setLoading(false); // readiness is on screen now

    // A page AI may not read, or that is not public, gets no paid questions.
    if (scan.extractionSkippedReason === 'ai-blocked' || scan.extractionSkippedReason === 'http-error') {
      setBlockedNotice(scan.extractionSkippedReason);
      return;
    }

    // 2) The live test, with the set chosen in the wizard.
    const queries = choice.set.queries.slice(0, plan === 'pro' ? 25 : freeCap);
    await runLiveTests(scan, queries, choice.set, choice.engine);
  }

  /**
   * The paid half of the flow: visibility + footprint, then persist the run.
   * `approved` is the client-approved question set in semi-automatic mode; when
   * absent the backend generates its own, as it always did.
   */
  async function runLiveTests(scan: ScanResult, approved?: string[], picked?: QuerySet, eng: 'openai' | 'gemini' = engine): Promise<void> {

    // No approved set (the free one-step path) — keep the old synchronous flow,
    // which is short enough to survive one request.
    if (!approved?.length) {
      const vis = await runProbe(undefined, scan.url);
      let fp: FootprintResult | null = null;
      if (vis.outcome !== 'limit' && vis.outcome !== 'blocked') fp = (await runFootprint(undefined, scan.url)).footprint;
      void saveScanRun({
        url: scan.url, engine, readinessScore: scan.readiness.score, readinessEngine: scan.model,
        readinessFull: scan, visibility: vis.probes, footprint: fp,
      }).then(() => setScanCount((c) => c + 1)).catch(() => {});
      // Land on the answer, not on the prediction. Readiness is what we could
      // guess before asking; Visibility is what actually happened, and leaving
      // somebody on the first tab makes them hunt for the result they waited for.
      if (vis.outcome !== 'limit' && vis.outcome !== 'blocked') setTab('visibility');
      return;
    }

    // An approved set is 25 questions — minutes of work. Run it as a background
    // job and poll: holding the request open is what produced
    // ERR_CONNECTION_CLOSED with nothing on screen.
    setProbeLoading(true);
    setProbeError(null);
    setFootprintError(null);
    setJobProgress({ done: 0, total: approved.length });
    try {
      // Identify the set so the server UPDATES it instead of inserting a new
      // one. Sending neither tells the server "this was a one-off, do not keep it".
      const started = await startProbeJob({
        url: scan.url, queries: approved, provider: eng,
        ...(picked ? { querySetId: Number(picked.id), querySetName: picked.name } : {}),
      });
      const job: ProbeJob = await waitForProbeJob(started.id, (j) => setJobProgress({ done: j.done, total: j.total }));

      if (job.status === 'failed' || !job.result) {
        setProbeError(job.error ?? 'the run did not finish');
        return;
      }
      const mapped = job.result.probes.map((p) => ({ ...p, origin: 'ai' as const }));
      setProbes(mapped);
      setProbeBrand(job.result.brand);
      setProbePages(job.result.analyzedPages ?? []);
      setProbeEngineLabel(job.result.engineLabel ?? null);
      setFootprint(job.result.footprint);
      setTab('visibility');

      const usedSet = picked;
      void saveScanRun({
        url: scan.url, engine: eng, readinessScore: scan.readiness.score, readinessEngine: scan.model,
        readinessFull: scan, visibility: mapped, footprint: job.result.footprint,
        // Name the set on the run itself, so the table and the PDF can answer
        // "which questions was this?" without decoding a hash.
        querySetId: usedSet?.id ?? null,
        querySetName: usedSet?.name ?? 'Generated for this run',
      }).then(() => setScanCount((c) => c + 1)).catch(() => {});
    } catch (err) {
      setProbeError(err instanceof Error ? err.message : 'the run could not be started');
    } finally {
      setProbeLoading(false);
      setJobProgress(null);
    }
  }

  async function refreshHistory(u: string): Promise<void> {
    try {
      setHistory(await getHistory(u));
    } catch {
      /* best-effort */
    }
  }

  async function onRescan(): Promise<void> {
    if (!result || rescanning) return;
    setRescanning(true);
    try {
      const fresh = await scanUrl(result.url, true);
      setResult(fresh);
      await refreshHistory(fresh.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'rescan failed');
    } finally {
      setRescanning(false);
    }
  }

  type RunOutcome = 'ok' | 'limit' | 'error' | 'blocked';
  async function runProbe(customQuery?: string, urlArg?: string, approved?: string[]): Promise<{ outcome: RunOutcome; probes: ProbeEntry[] }> {
    const targetUrl = urlArg ?? result?.url;
    if (!targetUrl || probeLoading) return { outcome: 'error', probes: [] };
    setProbeLoading(true);
    setProbeError(null);
    const origin: 'ai' | 'custom' = customQuery?.trim() ? 'custom' : 'ai';
    try {
      const r = await probeUrl(targetUrl, customQuery, questionCount, engine, approved);
      // Site blocks AI / no readable content — backend skipped the tokens (#3).
      if (r.skipped) {
        setBlockedNotice(r.skipped);
        setProbeBrand(r.brand);
        setProbePages(r.analyzedPages ?? []);
        return { outcome: 'blocked', probes: [] };
      }
      const mapped = r.result.probes.map((p) => ({ ...p, origin }));
      setProbes(mapped);
      setProbeBrand(r.brand);
      setProbePages(r.analyzedPages ?? []);
      setProbeEngineLabel(r.engineLabel ?? null);
      return { outcome: 'ok', probes: mapped };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'probe failed';
      setProbeError(msg);
      return { outcome: isLimitError(msg) ? 'limit' : 'error', probes: [] };
    } finally {
      setProbeLoading(false);
    }
  }

  async function onAddQuestion(q: string): Promise<void> {
    if (!result || adding) return;
    setAdding(true);
    setProbeError(null);
    try {
      const r = await probeUrl(result.url, q, undefined, engine);
      setProbes((prev) => [...prev, ...r.result.probes.map((p) => ({ ...p, origin: 'custom' as const }))]);
    } catch (err) {
      setProbeError(err instanceof Error ? err.message : 'probe failed');
    } finally {
      setAdding(false);
    }
  }

  async function runFootprint(brandName?: string, urlArg?: string, approved?: string[]): Promise<{ outcome: RunOutcome; footprint: FootprintResult | null }> {
    const targetUrl = urlArg ?? result?.url;
    if (!targetUrl || footprintLoading) return { outcome: 'error', footprint: null };
    setFootprintLoading(true);
    setFootprintError(null);
    try {
      // With an approved set, Visibility has already probed it — here we only want
      // the part Visibility cannot give: what AI recalls from training alone.
      const f = await footprintUrl(targetUrl, brandName, questionCount, engine, approved, Boolean(approved?.length));
      if (f.skipped) {
        setBlockedNotice(f.skipped);
        return { outcome: 'blocked', footprint: null };
      }
      setFootprint(f);
      return { outcome: 'ok', footprint: f };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'footprint failed';
      setFootprintError(msg);
      return { outcome: isLimitError(msg) ? 'limit' : 'error', footprint: null };
    } finally {
      setFootprintLoading(false);
    }
  }

  // Live scoreboard across all smer-B probes (visibility + footprint).
  const allProbes = [...probes, ...(footprint?.probes ?? [])];
  // The questions AI already answers with you in it — what actually worked.
  // Who gets named most across these answers. An absolute "4/25" says nothing on
  // its own — 4 is excellent if the leader has 5 and dismal if it has 20.
  const rivalCount = new Map<string, number>();
  for (const p of allProbes) for (const c of new Set(p.competitors ?? [])) rivalCount.set(c, (rivalCount.get(c) ?? 0) + 1);
  const topRival = [...rivalCount.entries()].sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count }))[0] ?? null;

  // Not every `cited_only` is fixable, and the difference is the most actionable
  // thing in the report. Rivals named means the model DOES write brand names for
  // that question and simply not yours — a page problem. Nobody named means it
  // answers that class of question with generic prose whatever your page says.
  const citedProbes = allProbes.filter((p) => p.state === 'cited_only');
  const citedFixable = citedProbes.filter((p) => (p.competitors ?? []).length > 0).length;
  const citedUnwinnable = citedProbes.length - citedFixable;

  const wins = allProbes
    .filter((p) => p.state === 'mentioned' || p.state === 'cited_only')
    .map((p) => ({ query: p.query, state: p.state as 'mentioned' | 'cited_only', citations: p.citations, detail: p.detail }));
  const scMentioned = allProbes.filter((p) => p.state === 'mentioned').length;
  const scCited = allProbes.filter((p) => p.state === 'cited_only').length;
  const scKnows = footprint?.knowledge.knows ?? null;

  // Questions available for the FAQ builder: prefer visibility probes, else footprint.
  const fixQuestions = probes.length
    ? probes.map((p) => p.query)
    : footprint
      ? footprint.probes.map((p) => p.query)
      : [];
  // Questions where AI didn't name you — the highest-leverage content to publish (#6d).
  const absentQuestions = [...new Set(
    [...probes, ...(footprint?.probes ?? [])].filter((p) => p.state === 'absent').map((p) => p.query),
  )];

  // Tabs appear progressively — each one only once its section has finished, so the
  // visitor isn't faced with empty tabs. Fix it shows once the whole flow is done.
  const flowDone = !loading && !probeLoading && !footprintLoading;
  // Each tab carries its own live figure, so the bar doubles as a summary and
  // you can tell what's worth opening without opening it.
  const tabs: TabDef[] = [
    { id: 'readiness', label: 'Readiness', value: result ? `${result.readiness.score}/100` : undefined },
  ];
  if (probes.length > 0) {
    const named = probes.filter((p) => p.state === 'mentioned').length;
    tabs.push({ id: 'visibility', label: 'Visibility', value: `${named}/${probes.length}` });
  }
  if (footprint) {
    tabs.push({ id: 'footprint', label: 'Footprint', value: footprint.knowledge.knows === 'no' ? 'unknown' : 'knows you' });
  }
  if (flowDone && (probes.length > 0 || footprint)) {
    tabs.push({ id: 'fixit', label: 'Fix it', value: wins.length > 0 ? `${wins.length} won` : undefined });
  }

  // Badge showing which AI engine actually ran the probe/footprint.
  const engineBadge = (label: string | null | undefined): JSX.Element | null =>
    label ? (
      <div className="font-mono text-sm text-text-secondary flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-cyan" /> answered via {engineName(label)}
      </div>
    ) : null;

  return (
    <main className={embedded ? 'flex flex-col items-center gap-8' : 'px-6 py-12 xl:pr-80 flex flex-col items-center gap-8'}>
      <header className="text-center">
        <p className="font-mono text-sm uppercase tracking-widest text-cyan mb-3">Product</p>
        <h1 className="font-serif text-4xl md:text-5xl mb-3">AI Visibility Scan</h1>
        <p className="text-text-secondary text-sm max-w-lg mx-auto leading-relaxed">
          Does AI see you and can it cite you?<br />
          Enter a URL and get an honest AIO diagnosis. No promises.
        </p>
        {/* w-fit so the bubble/arrow anchor to the buttons, not the full-width row (#2) */}
        <div className="mt-8 flex flex-wrap w-fit max-w-full mx-auto items-center justify-center gap-3 relative">
          {/* Read-first bubble with a curved dashed arrow to the "How AI citation" button (#2) */}
          {!hasReadAbout && !showAbout && !showPricing && !result && (
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 md:top-1/2 md:left-auto md:right-full md:translate-x-0 md:-translate-y-1/2 md:mr-12 w-56">
              <div className="animate-bounce-slow bg-cyan/10 border border-cyan/40 rounded-lg px-3 py-2 text-sm text-cyan text-left">
                New here? Read this first. It explains what actually gets you cited.
              </div>
              {/* Desktop: straight dashed arrow pointing right into the button */}
              <svg className="hidden md:block absolute top-1/2 -translate-y-1/2 left-full ml-[0.4rem] pointer-events-none" width="44" height="16" viewBox="0 0 44 16" fill="none" aria-hidden="true">
                <path d="M1 8 L 40 8" stroke="#00FFD4" strokeWidth="2" strokeDasharray="4 4" />
                <path d="M40 8 l -8 -5 M40 8 l -8 5" stroke="#00FFD4" strokeWidth="2" />
              </svg>
              {/* Mobile: straight dashed arrow pointing down to the button */}
              <svg className="md:hidden mx-auto mt-1 pointer-events-none" width="16" height="30" viewBox="0 0 16 30" fill="none" aria-hidden="true">
                <path d="M8 1 L 8 26" stroke="#00FFD4" strokeWidth="2" strokeDasharray="4 4" />
                <path d="M8 26 l -5 -8 M8 26 l 5 -8" stroke="#00FFD4" strokeWidth="2" />
              </svg>
            </div>
          )}
          <button
            onClick={() => (showAbout ? setShowAbout(false) : openAbout())}
            className="font-mono text-sm text-cyan border border-cyan/40 rounded px-4 py-1.5 hover:bg-cyan/10 transition"
          >
            {showAbout ? '← back to scan' : 'How AI citation really works →'}
          </button>
          <button
            onClick={() => { setShowPricing((s) => !s); setShowAbout(false); }}
            className="font-mono text-sm text-text-secondary border border-warm-dark-2 rounded px-4 py-1.5 hover:text-cyan hover:border-cyan/40 transition"
          >
            {showPricing ? '← back to scan' : 'Pricing'}
          </button>
        </div>
      </header>

      {showAbout && <About />}
      {showPricing && <Pricing plan={plan} onGoPro={() => { setShowPricing(false); setModal(account ? 'order' : 'signin'); }} />}

      {!showAbout && !showPricing && (
      <>
      {/* A limit was hit. Which limit matters: telling a Pro account it "hit the
          free limit" and offering an upgrade is both wrong and insulting. It
          read as "make Pro doesn't work". Show what the server actually said,
          and only pitch the upgrade when upgrading is the answer. */}
      {limitHit && (
        <div className="w-full max-w-2xl border border-cyan/40 rounded-xl p-5 text-center bg-cyan/5">
          <p className="font-serif text-lg mb-1">
            {plan === 'pro' ? 'Fair-use limit reached' : "You've hit the free limit"}
          </p>
          <p className="text-sm text-text-secondary mb-3">
            {limitMessage
              ? limitMessage
              : plan === 'pro'
                ? 'Your Pro allowance for this period is used up.'
                : `Go Pro (€${pricing.monthlyEur}/mo, everything) for more scans, the full visibility & footprint, and the Fix it plan.`}
          </p>
          {plan === 'pro' ? (
            <p className="font-mono text-xs text-text-secondary/80">
              Need more? Ask us to raise the allowance on your account.
            </p>
          ) : (
          <button
            onClick={() => setShowPricing(true)}
            className="bg-cyan text-warm-dark font-medium rounded-lg px-6 py-2.5 text-sm hover:opacity-90 transition"
          >
            See Pricing →
          </button>
          )}
        </div>
      )}

      {/* The scan, as a wizard (site → questions → engine → run). Open on the
          landing page; with a result on screen it folds to one summary line so
          the result is what you see, and opens again for the next scan. */}
      {(!result && !loading) || wizardOpen ? (
        <ScanWizard
          key={wizardOpen ? 'again' : 'first'}
          signedIn={Boolean(account)}
          plan={plan}
          engines={engines}
          scannedDomains={scannedDomains}
          freeCap={freeCap}
          initial={{ url: result ? `https://${runSet?.domain ?? hostOfUrl(result.url)}/` : url, setId: runSet?.id ?? null, engine }}
          busy={loading || probeLoading || footprintLoading}
          onRun={onWizardRun}
          onNeedSignIn={() => setModal('signin')}
          onClose={result ? () => setWizardOpen(false) : undefined}
        />
      ) : (
        <div className="w-full max-w-5xl flex items-center justify-between gap-3 flex-wrap border border-warm-dark-2 rounded-xl px-4 py-3">
          <p className="font-mono text-sm text-text-secondary min-w-0 truncate">
            <span className="text-text-primary">{result ? hostOfUrl(result.url) : hostOfUrl(url)}</span>
            {runSet && <> · {runSet.name}</>}
            {' · '}{engine === 'openai' ? 'ChatGPT' : engineName(engine)}
          </p>
          <button
            onClick={() => setWizardOpen(true)}
            disabled={loading || probeLoading || footprintLoading}
            className="bg-cyan text-warm-dark font-medium rounded-lg px-5 py-2 text-sm hover:opacity-90 disabled:opacity-40 transition"
          >
            New scan
          </button>
        </div>
      )}

      {/* Landing hero storyboard, before any scan */}
      {!result && !loading && (
        <>
          <p className="text-center text-sm text-text-secondary max-w-2xl mt-2 leading-relaxed">
            Most tools sell you AI mentions. PageCoder tells you the honest truth: where AI already cites you, where it names
            competitors instead, and exactly how to close the gap yourself. Diagnose, fix, re-measure. The loop you own.
          </p>
          <HeroLoop />
        </>
      )}

      {error && !isLimitError(error) && (
        <p className="font-mono text-sm text-[#E0574A] border border-[#E0574A]/40 rounded-lg px-4 py-3">{friendlyError(error)}</p>
      )}

      {loading && !result && <ScanProgress label="Checking how AI reads this page" steps={scanSteps} />}

      {result && (
        <>
          <Tabs tabs={tabs} active={tab} onChange={(id) => setTab(id as TabId)} />

          {/* Site blocks AI (or no readable content) — the live AI tests were
              skipped before spending any tokens (#3). */}
          {blockedNotice && (
            <div className="w-full max-w-5xl border border-estimated/50 rounded-xl p-5 bg-estimated/5">
              <p className="font-serif text-lg mb-1">
                {blockedNotice === 'ai-blocked'
                  ? 'This site blocks AI crawlers, so live tests were skipped'
                  : blockedNotice === 'http-error'
                    ? "This page isn't publicly readable, so live tests were skipped"
                    : "Couldn't read enough content, so live tests were skipped"}
              </p>
              <p className="text-sm text-text-secondary">
                {blockedNotice === 'ai-blocked'
                  ? 'Its robots.txt disallows the AI bots that cite you (GPTBot, ClaudeBot, PerplexityBot, Google-Extended). The scan stopped before spending any AI tokens, because the live Visibility and Footprint tests would only measure a page AI cannot read. Fix step 1 (unblock) shown in Readiness, then re-scan.'
                  : blockedNotice === 'http-error'
                    ? 'The page returned an HTTP error (e.g. 401 / 403) or sits behind a login / consent wall, so only a block page came back rather than your real content. The scan stopped before spending any AI tokens. Make sure the URL is publicly reachable and returns 200, then re-scan.'
                    : 'The page returned too little readable text, usually a cookie, consent or anti-bot wall. The content analysis was skipped to avoid generating questions unrelated to your real content. Make sure the page serves real content to a plain fetch, then re-scan.'}
              </p>
            </div>
          )}

          {/* Progressive full-scan status: readiness shows first, then the live AI
              tests auto-run; when done, nudge toward Fix it. */}
          {(probeLoading || footprintLoading) ? (
            <div className="w-full max-w-5xl -mt-1 flex items-center justify-center gap-2 font-mono text-sm text-text-secondary">
              <span className="inline-block w-3 h-3 border-2 border-cyan/40 border-t-cyan rounded-full animate-spin" />
              {jobProgress
                ? <>Asking AI your {jobProgress.total} questions, {jobProgress.done} of {jobProgress.total} done. This takes a few minutes; you can leave the page, the results are saved and emailed to you.</>
                : <>Running live AI tests (web search, ~30s) · Visibility {probeLoading ? '…' : probes.length ? '✓' : '-'} · Footprint {footprintLoading ? '…' : footprint ? '✓' : '-'}</>}
            </div>
          ) : (probes.length > 0 || footprint) && tab !== 'fixit' ? (
            <button
              onClick={() => setTab('fixit')}
              className="w-full max-w-5xl -mt-2 text-center font-mono text-sm text-cyan border border-cyan/30 bg-cyan/5 rounded-lg px-4 py-2 hover:bg-cyan/10 transition"
            >
              ✓ Scan complete. Open <span className="underline">Fix it</span> for concrete steps to improve →
            </button>
          ) : null}


          {/* The headline numbers, as tiles rather than a strip of dots. Each
              carries its own caveat — a number without one is a claim. */}
          {(allProbes.length > 0 || scKnows !== null) ? (
            <div className="w-full max-w-5xl">
              <SectionLabel>Result</SectionLabel>
              <div className="h-2" />
              <StatRow>
                <Stat info="readiness" label="Readiness" value={result.readiness.score} unit="/100"
                  tone={result.readiness.score >= 70 ? 'good' : result.readiness.score >= 40 ? 'warn' : 'bad'}
                  note="Can AI read and understand this page. A prediction, not visibility" />
                <Stat info="cited_only" label="Fixable on your pages" value={`${citedFixable}`}
                  tone={citedFixable > 0 ? 'warn' : 'neutral'}
                  note={citedFixable > 0
                    ? 'AI read your page and named a rival. A sentence away'
                    : 'No question is losing purely on page wording'} />
                <Stat info="mentioned" label="AI named you" value={`${scMentioned}/${allProbes.length}`}
                  tone={scMentioned > 0 ? 'good' : 'bad'}
                  note="Questions where the answer says your name" />
                <Stat info="share_of_voice" label="Share of voice" value={topRival ? `${scMentioned} vs ${topRival.count}` : `${scMentioned}`}
                  tone={topRival && scMentioned >= topRival.count ? 'good' : topRival ? 'bad' : 'neutral'}
                  note={topRival ? `You against ${topRival.name}, the most-named brand in these answers` : 'No competitor was named either'} />
                <Stat info="footprint" label="Knows you unprompted" value={scKnows ? (scKnows === 'no' ? 'No' : scKnows === 'well' ? 'Well' : 'Vaguely') : '-'}
                  tone={scKnows === 'no' ? 'bad' : scKnows ? 'good' : 'neutral'}
                  note="From training memory alone, no page in front of it" />
              </StatRow>
              {/* `cited` is not a second score to grow — it is the diagnosis of a
                  LOSS, and a different one from being absent. Retrieved but not
                  named is an on-page brand-clarity problem; not retrieved at all
                  is an off-site one. Shown as a breakdown so it cannot read as
                  "0 cited is a bad number". */}
              {allProbes.length > scMentioned && (
                <p className="text-sm text-text-secondary mt-2">
                  Of the {allProbes.length - scMentioned} you didn&apos;t win:{' '}
                  <span className="text-estimated">{scCited}</span> used your page as a source but didn&apos;t name you
                  {citedFixable > 0 && (
                    <>. <span className="text-text-primary">{citedFixable} of those named a rival instead</span>, which is
                    a sentence on your page{citedUnwinnable > 0 ? `, and ${citedUnwinnable} named nobody at all, which no wording wins` : ''}</>
                  )}
                  {citedFixable === 0 && ' (a brand-clarity problem on the page)'}, and{' '}
                  <span className="text-[#E0574A]">{allProbes.length - scMentioned - scCited}</span> didn&apos;t reach
                  you at all (an off-site presence problem). Different questions, different fixes.
                </p>
              )}
            </div>
          ) : (
            <Scoreboard mentioned={scMentioned} cited={scCited} total={allProbes.length} knows={scKnows} />
          )}

          {/* Persistent explanation of the active section + its scope */}
          <div className="w-full max-w-5xl -mt-2 text-sm text-text-secondary border-l-2 border-warm-dark-2 pl-3">
            {tab === 'readiness' && (
              <p>
                <span className="text-text-primary">Readiness: this page.</span> Technical AIO readiness of the exact
                URL you scanned: can AI read, understand and cite <em>this page</em>? A prediction, not real visibility.
              </p>
            )}
            {tab === 'visibility' && (
              <p>
                <span className="text-text-primary">Visibility: your brand, across your key pages.</span> This reads your
                main service/product pages (not just this URL), generate the real questions buyers ask, and ask AI live
                whether it names you. This is reality, not a prediction.
              </p>
            )}
            {tab === 'footprint' && (
              <p>
                <span className="text-text-primary">Footprint: does AI know your brand at all?</span> From memory
                (cold) vs while reading your pages (warm), and where you already have a real foothold. Brand-level,
                across your key pages.
              </p>
            )}
            {tab === 'fixit' && (
              <p>
                <span className="text-text-primary">Fix it: your prioritised action plan.</span> Concrete steps in the
                right order: unblock AI, sharpen the page, publish answers to the real buyer questions, then build the
                off-site presence that actually decides citation.
              </p>
            )}
          </div>

          {tab === 'readiness' && (
            <>
              <Dashboard result={result} />
              {history && <Timeline history={history} onRescan={onRescan} rescanning={rescanning} />}
            </>
          )}

          {tab === 'visibility' && (
            <div className="w-full flex flex-col items-center gap-4">
              {probes.length === 0 && !probeLoading && !probeError && (
                <div className="w-full max-w-5xl border border-cyan/30 rounded-xl p-5 text-center">
                  <h2 className="font-serif text-lg mb-1">
                    Visibility <span className="text-cyan">the real result</span>
                  </h2>
                  <p className="text-sm text-text-secondary mb-4">
                    Realistic questions are generated from your page (in its own language) and actually put to AI to see whether it
                    mentions you. This uses live web search (~20s).
                  </p>
                  <button
                    onClick={() => runProbe()}
                    className="bg-cyan text-warm-dark font-medium rounded-lg px-6 py-2.5 text-sm hover:opacity-90 transition"
                  >
                    Run visibility probe →
                  </button>
                </div>
              )}
              {probeLoading && (
                <ScanProgress
                  label="Asking AI (10 questions, web search)"
                  stages={[
                    'Generating 10 questions from your page',
                    'Running live web searches',
                    'Detecting mentions vs citations',
                    'Mapping competitors',
                  ]}
                />
              )}
              {probeError && !isLimitError(probeError) && (
                <p className="font-mono text-sm text-estimated border border-estimated/40 rounded-lg px-4 py-3">
                  visibility probe: {friendlyError(probeError)}
                </p>
              )}
              {probes.length > 0 && (
                <>
                  {engineBadge(probeEngineLabel)}
                  <PanelBoundary name="Visibility"><VisibilityPanel
                    probes={probes}
                    brand={probeBrand}
                    analyzedPages={probePages}
                    onAddQuestion={onAddQuestion}
                    adding={adding}
                    onShowAbout={() => setShowAbout(true)}
                  /></PanelBoundary>
                  {/* Who is actually winning, added up across the run — each
                      question listed its own rivals and nothing totalled them. */}
                  {probeBrand && (
                    <PanelBoundary name="Who AI names instead of you">
                      <RunRivals probes={probes} domain={probeBrand.domain} />
                    </PanelBoundary>
                  )}
                  {/* The one loss with a same-day fix, on a page the client owns.
                      Sits directly under the results because "which of my pages,
                      and what do I change on it" is the question the states
                      raise and nothing was answering. */}
                  {probeBrand && (
                    <ClaimYourPages
                      probes={probes}
                      brand={probeBrand}
                      lang={null}
                      isPro={plan === 'pro'}
                    />
                  )}

                  {/* What to do next, at the end of the result rather than
                      floating above every tab. Somebody reading the rivals has
                      just worked out what is wrong; that is the moment the next
                      two steps make sense, not before they have seen anything. */}
                  {flowDone && (
                    <div className="w-full max-w-5xl mt-2">
                      <SectionLabel>Keep going</SectionLabel>
                      <div className="grid lg:grid-cols-2 gap-4 mt-3 items-start">
                        <PagesPanel url={result.url} />
                        {account && <ScheduleToggle set={runSet} engine={engine} plan={plan} email={account.email} />}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {tab === 'footprint' && (
            <div className="w-full flex flex-col items-center gap-4">
              {!footprint && !footprintLoading && !footprintError && (
                <div className="w-full max-w-2xl border border-warm-dark-2 rounded-xl p-5 text-center">
                  <h2 className="font-serif text-lg mb-1">Where AI already knows you</h2>
                  <p className="text-sm text-text-secondary mb-4">
                    The model is asked what it knows about your brand from training, then its suggestions are verified in live
                    search, to see where you already have a real foothold.
                  </p>
                  <button
                    onClick={() => runFootprint()}
                    className="bg-cyan text-warm-dark font-medium rounded-lg px-6 py-2.5 text-sm hover:opacity-90 transition"
                  >
                    Check footprint →
                  </button>
                </div>
              )}
              {footprintLoading && (
                <ScanProgress
                  label="Checking where AI knows you"
                  stages={[
                    'Asking the model what it knows about your brand',
                    'Collecting its suggested questions',
                    'Verifying them in live web search',
                    'Confirming real footholds',
                  ]}
                />
              )}
              {footprintError && !isLimitError(footprintError) && (
                <p className="font-mono text-sm text-estimated border border-estimated/40 rounded-lg px-4 py-3">
                  footprint: {friendlyError(footprintError)}
                </p>
              )}
              {footprint && (
                <>
                  {engineBadge(footprint.engineLabel)}
                  <PanelBoundary name="Footprint">
                    <FootprintPanel
                      footprint={footprint}
                      onRecheck={(name) => runFootprint(name)}
                      rechecking={footprintLoading}
                      onShowAbout={() => setShowAbout(true)}
                    />
                  </PanelBoundary>
                </>
              )}
            </div>
          )}

          {tab === 'fixit' && plan === 'free' && (
            <div className="w-full max-w-2xl border-2 border-cyan/40 rounded-xl p-6 text-center bg-cyan/5">
              <p className="font-mono text-sm uppercase tracking-widest text-cyan mb-1">Pro feature</p>
              <p className="font-serif text-xl mb-2">The Fix it action plan is a Pro feature</p>
              <p className="text-sm text-text-secondary mb-4">
                The prioritised plan, FAQ drafting from your content, the channel-linking guide and all the how-to steps
                are included in Pro (€{pricing.monthlyEur}/mo, everything). The free test gives you the diagnosis; Pro gives you the plan.
              </p>
              <button
                onClick={() => setShowPricing(true)}
                className="bg-cyan text-warm-dark font-medium rounded-lg px-6 py-2.5 text-sm hover:opacity-90 transition"
              >
                See Pricing →
              </button>
            </div>
          )}

          {tab === 'fixit' &&
            plan === 'pro' &&
            (probes.length > 0 && footprint ? (
              <FixIt result={result} questions={fixQuestions} faq={faq} setFaq={setFaq} absentQuestions={absentQuestions} wins={wins} />
            ) : (
              <div className="w-full max-w-2xl border border-estimated/40 rounded-xl p-5 bg-estimated/5">
                <p className="font-serif text-lg mb-1">Run Visibility and Footprint first</p>
                <p className="text-sm text-text-secondary mb-3">
                  The action plan is built from your real results. Run both scans so the plan knows where you're absent and
                  what to fix, then this fills in.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setTab('visibility')}
                    className={`font-mono text-sm rounded px-3 py-1 border ${probes.length ? 'text-success border-success/40' : 'text-cyan border-cyan/40 hover:bg-cyan/10'}`}
                  >
                    {probes.length ? '✓ Visibility done' : 'Go to Visibility →'}
                  </button>
                  <button
                    onClick={() => setTab('footprint')}
                    className={`font-mono text-sm rounded px-3 py-1 border ${footprint ? 'text-success border-success/40' : 'text-cyan border-cyan/40 hover:bg-cyan/10'}`}
                  >
                    {footprint ? '✓ Footprint done' : 'Go to Footprint →'}
                  </button>
                </div>
              </div>
            ))}
        </>
      )}

      {/* RAG Chat bundle CTA — always visible (landing + after results, signed-in too),
          shown right above the FAQ (#3). */}
      {/* Once there is a result the visitor is using a tool, not reading a pitch —
          so the sales tail collapses instead of adding 900px of scroll under it. */}
      {!result && (
      <div className="w-full max-w-2xl border border-cyan/40 rounded-xl px-5 py-4 bg-cyan/5 flex flex-col sm:flex-row items-center gap-3 text-center sm:text-left">
        <p className="text-sm text-text-secondary flex-1">
          <span className="text-cyan font-medium">Bonus:</span> take <span className="text-text-primary">PageCoder RAG Chat</span>{' '}
          for six months or more and{' '}
          <span className="text-text-primary">your first month of Pro here is free</span>. Pro is billed normally after that,
          and the free scan stays free either way.
        </p>
        <a
          href="https://pagecoder.ai/products/rag-chat/"
          target="_blank"
          rel="noreferrer"
          className="shrink-0 font-mono text-sm text-cyan border border-cyan/40 rounded-lg px-4 py-2 hover:bg-cyan/10 transition"
        >
          Explore RAG Chat ↗
        </a>
      </div>

      )}

      {/* FAQ — open on the landing page, folded away under a result. The sidebar
          "FAQ" link still reaches it either way. */}
      <div id="faq" className="w-full max-w-2xl scroll-mt-28">
        {result ? (
          <details>
            <summary className="font-mono text-sm text-text-secondary hover:text-cyan cursor-pointer text-center">
              Frequently asked questions ↓
            </summary>
            <div className="mt-2"><Faq /></div>
          </details>
        ) : (
          <Faq />
        )}
      </div>
      </>
      )}

      {/* Recent scans + nav menu (sticky sidebar on xl, inline below on mobile).
          Not inside the dashboard: the rail there does this job. */}
      {account && !embedded && (
        <RecentScans
          refreshKey={scanCount}
          onScan={goToScan}
          onAbout={openAbout}
          onFaq={goToFaq}
          onProgress={() => { window.location.hash = 'account'; }}
        />
      )}

      {/* Read-first modal (#2), shown once, only if they scan before reading */}
      {readModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm px-6" onClick={() => { setReadModal(false); setPendingRun(null); }}>
          <div className="w-full max-w-md rounded-xl border border-cyan/40 bg-warm-dark p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <p className="font-serif text-xl mb-2">Read this first (2 minutes)</p>
            <p className="text-sm text-text-secondary mb-5">
              Before you scan, it helps to understand what actually gets you cited by AI, and why a low score isn't a
              verdict. It'll make your result far more useful.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <button onClick={openAbout} className="bg-cyan text-warm-dark font-medium rounded-lg px-5 py-2.5 text-sm hover:opacity-90 transition">
                Read “How AI citation works” →
              </button>
              <button
                onClick={() => { markRead(); if (pendingRun) void doScan(pendingRun); }}
                className="font-mono text-sm text-text-secondary border border-warm-dark-2 rounded-lg px-5 py-2.5 hover:text-text-primary transition"
              >
                Scan anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Big overlay while the live AI tests run so it's clear work is happening (#4). */}
      {(probeLoading || footprintLoading) && (
        <LiveTestOverlay phase={probeLoading ? 'visibility' : 'footprint'} />
      )}

      <FontSize />
    </main>
  );
}
